import React, { Fragment, useEffect, useState } from 'react';
import { Button, Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Input } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { requestApproval, fetchOrder, orderRevert, amendScreeningDate } from '../../slices/orders.slice';
import { fetchOrderKDM } from '../../slices/content.slice';
import Loading from '../../components/Loading';
import { imgUrl } from '../../utils/api-request';
import OrderStatusBadge from '../../components/OrderStatusBadge';
import OrganizationPatch from '../../components/OrganizationPatch';
import ClickToCopy from '../../components/ClickToCopy';
import { formatCredits } from '../../utils/credits';
import { toast } from 'react-toastify';
import HireCountdown from '../../components/HireCountdown';
import { DateTimeField } from '../../components/DateTime';
import PurchaseHistory from '../../components/PurchaseHistory';
import PurchaseTimeline from '../../components/PurchaseTimeline';
import PurchaseTerms from '../../components/PurchaseTerms';
import PurchaseSelector from '../../components/PurchaseSelector';

const OrderDetailsBAC = () => {
    const { orderId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentOrder: order, status } = useSelector((state) => state.orders);
    const [searchParams, setSearchParams] = useSearchParams();
    const [revertModal, setRevertModal] = useState(false);
    const [approvalModal, setApprovalModal] = useState({ isOpen: false, operation: null });
    const [amendModal, setAmendModal] = useState(false);
    const [amendDate, setAmendDate] = useState(null);
    const [amendNotes, setAmendNotes] = useState('');
    const [kdm, setKdm] = useState(null);
    const [kdmLoading, setKdmLoading] = useState(false);
    const [revertReason, setRevertReason] = useState('');
    const [declineReason, setDeclineReason] = useState('');
    const [screeningTerms, setScreeningTerms] = useState('');
    const [bacCorrection, setBacCorrection] = useState({
        defaultHirePeriodCorrection: null,
        hirePeriodTypeCorrection: null,
        screeningDateCorrection: null,
        priceCorrection: '',
    });

    const openAmendModal = () => {
        setAmendDate(order.screeningDate ? new Date(order.screeningDate) : new Date());
        setAmendNotes('');
        setKdm(null);
        setAmendModal(true);

        if (order.uuid && order.organization?._id) {
            setKdmLoading(true);
            dispatch(fetchOrderKDM({ uuid: order.uuid, organization: order.organization._id }))
                .unwrap()
                .then(result => setKdm(result))
                .catch(() => setKdm(null))
                .finally(() => setKdmLoading(false));
        }
    };

    const handleAmend = () => {
        if (!amendDate) return;
        const promise = dispatch(amendScreeningDate({
            orderId,
            body: { screeningDate: amendDate.toISOString(), notes: amendNotes || undefined },
        })).unwrap();
        void toast.promise(promise, {
            pending: 'Amending screening date',
            success: 'Screening date amended successfully',
            error: 'Error amending screening date',
        });
        promise.then(() => setAmendModal(false)).catch(() => {});
    };

    const formatInTimezone = (date, timezone) => {
        if (!date) return '—';
        try {
            return new Intl.DateTimeFormat(undefined, {
                timeZone: timezone || undefined,
                dateStyle: 'full',
                timeStyle: 'short',
            }).format(new Date(date));
        } catch {
            return new Date(date).toLocaleString();
        }
    };

    const isOutsideKdmWindow = () => {
        if (!kdm || !amendDate) return false;
        const t = amendDate.getTime();
        const before = kdm.KDM_notValidBefore ? new Date(kdm.KDM_notValidBefore).getTime() : null;
        const after = kdm.KDM_notValidAfter ? new Date(kdm.KDM_notValidAfter).getTime() : null;
        return (before !== null && t < before) || (after !== null && t > after);
    };

    const handleRevert = () => {
        const promise = dispatch(orderRevert({ orderId, body: { revertReason } })).unwrap();
        void toast.promise(promise, {
            pending: 'Reverting order',
            success: 'Order reverted successfully',
            error: 'Error reverting order',
        });
        setRevertModal(false);
    };

    const handleRequestApproval = () => {
        let promise;
        if (approvalModal.operation === 'approve') {
            const body = { screeningTerms, bacCorrection };
            promise = dispatch(requestApproval({ orderId, operation: 'approve', body })).unwrap();
        }

        if (approvalModal.operation === 'decline') {
            const body = { declineReason };
            promise = dispatch(requestApproval({ orderId, operation: 'decline', body })).unwrap();
        }

        void toast.promise(promise, {
            pending: 'Updating order',
            success: 'Order updated successfully',
            error: 'Error updating order',
        });
        setApprovalModal({ isOpen: false, operation: null });
    };

    const toggleApprovalModal = operation => {
        setApprovalModal(prevState => ({
            isOpen: !prevState.isOpen,
            operation: prevState.isOpen ? null : operation
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setBacCorrection({
            ...bacCorrection,
            [name]: value,
        });
    };

    const isRevertible = () => {
        return !['playing', 'finished-playback'].includes(order.status);
    };

    useEffect(() => {
        dispatch(fetchOrder(orderId));
    }, [dispatch, orderId]);

    useEffect(() => {
        if (order && order.bacCorrection) {
            // If bacCorrection already exists, populate with existing values
            setBacCorrection({
                defaultHirePeriodCorrection: order.bacCorrection.defaultHirePeriodCorrection || order.version.defaultHirePeriod,
                hirePeriodTypeCorrection: order.bacCorrection.hirePeriodTypeCorrection || order.version.hirePeriodType,
                screeningDateCorrection: order.bacCorrection.screeningDateCorrection ? new Date(order.bacCorrection.screeningDateCorrection) : new Date(order.screeningDate),
                priceCorrection: order.bacCorrection.priceCorrection || '',
            });
        } else if (order) {
            // For new corrections, don't pre-fill with defaults - leave empty/null
            // This ensures the comparison will properly show when values differ
            setBacCorrection({
                defaultHirePeriodCorrection: null,
                hirePeriodTypeCorrection: null,
                screeningDateCorrection: order.screeningDate ? new Date(order.screeningDate) : new Date(),
                priceCorrection: '',
            });
        }
    }, [order]);

    if (['idle', 'loading'].includes(status)) {
        return <Loading fullHeight />;
    }

    // An order document is bought over and over, so the panels below describe
    // ONE purchase — the latest by default, or whichever `?purchase=N` names.
    // Past Orders links straight to a row's own purchase, and landing on the
    // newest one instead would be the same "shows the current state" confusion
    // this whole change is about.
    const purchases = order?.purchases || [];
    // Not Number(get(...)) — an absent param is null, and Number(null) is 0,
    // which would silently open the FIRST purchase instead of the latest.
    const requested = searchParams.has('purchase') ? Number(searchParams.get('purchase')) : null;
    const selectedIndex = purchases.some(p => p.index === requested) ? requested : purchases.length - 1;
    const currentPurchase = purchases[selectedIndex] || null;
    // Actions write to the document, which only ever holds the latest purchase.
    // Offering Revert or Accept while an earlier one is on screen would act on
    // a booking the user is not looking at.
    const isLatestPurchase = selectedIndex === purchases.length - 1;

    const selectPurchase = (index) => {
        const params = new URLSearchParams(searchParams);
        if (index === purchases.length - 1) params.delete('purchase');
        else params.set('purchase', index);
        setSearchParams(params, { replace: true });
    };

    return status === 'success' && order && (
        <div className="order-view page p-4">
            {/* Back Navigation */}
            <div className="mb-3">
                <NavLink onClick={() => navigate(-1)} className="text-decoration-none text-white">
                    <span className="fas fa-chevron-left me-2" />
                    <span>Back to Orders</span>
                </NavLink>
            </div>

            {/* Main Order Card */}
            <div className="bg-broken border-start border-primary border-4 p-3 mb-4 shadow-sm">
                <Row className="g-3 align-items-center">
                    {/* Movie Poster */}
                    <Col md="auto">
                        {order.movie.poster ? (
                            <img
                                className="rounded shadow"
                                style={{ width: '120px', height: '180px', objectFit: 'cover' }}
                                alt={order.movie.title}
                                src={imgUrl(order.movie.poster.imageUrl)}
                            />
                        ) : (
                            <img
                                className="rounded opacity-25"
                                style={{ width: '120px', height: '180px', objectFit: 'cover' }}
                                alt={order.movie.title}
                                src={`https://picsum.photos/seed/${order.movie.title}/120/180`}
                            />
                        )}
                    </Col>

                    {/* Movie & Order Info */}
                    <Col>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <span className="badge bg-secondary text-uppercase px-2 py-1">
                                {order.orderType}
                            </span>
                            <OrderStatusBadge status={order.status} isCard className="d-inline-flex" />
                        </div>

                        <h2 className="text-white mb-1" style={{ fontSize: '1.75rem', fontWeight: '600' }}>
                            {order.movie.title}
                        </h2>
                        <div className="text-white-50 mb-3" style={{ fontSize: '0.95rem' }}>
                            <div>
                                <strong className="text-primary">Version name: </strong> {order.version.name}
                            </div>
                            <div>
                                <strong className="text-primary">Version ID: </strong>
                                <ClickToCopy
                                    text={order.version._id}
                                    className="text-white"
                                    style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}
                                />
                            </div>
                        </div>

                        <div className="mt-3">
                            <strong className="text-primary">UUID: </strong>
                            <ClickToCopy
                                text={order.uuid}
                                className="text-white"
                                style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}
                            />
                        </div>

                    </Col>

                    <Col md="auto" className="text-end">
                        <OrganizationPatch organization={order.organization} />
                        {['ready-on-mediablock', 'finished-playback'].includes(order.statusWorkflow) && (
                            <HireCountdown order={order} />
                        )}
                    </Col>
                </Row>
            </div>

            <Row className="g-3">
                {/* Order Details Section */}
                <Col lg={6}>
                    <div className="bg-broken border-start border-primary border-3 p-3 h-100 shadow-sm">
                        <h5 className="text-primary mb-3 d-flex align-items-center">
                            <span className="fas fa-info-circle me-2" />
                            Order Details
                        </h5>

                        <PurchaseSelector
                            purchases={purchases}
                            selectedIndex={selectedIndex}
                            onSelect={selectPurchase}
                        />

                        <Row className="g-3">
                            <Col md={6}>
                                <div className="mb-3">
                                    <div className="text-white-50 small mb-1">Order ID</div>
                                    <ClickToCopy
                                        text={order._id}
                                        className="text-white"
                                        style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}
                                    />
                                </div>

                                <PurchaseTerms purchase={currentPurchase} version={order.version} />
                            </Col>

                            <Col md={6}>
                                <div className="mb-3">
                                    <div className="text-white-50 small mb-1">Purchased</div>
                                    <div className="text-white" style={{ fontSize: '0.85rem' }}>
                                        {currentPurchase?.purchasedAt
                                            ? new Date(currentPurchase.purchasedAt).toLocaleString()
                                            : '—'}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="text-white-50 small mb-1">Last Updated</div>
                                    <div className="text-white" style={{ fontSize: '0.85rem' }}>
                                        {new Date(order.updatedAt).toLocaleString()}
                                    </div>
                                </div>

                                {order.version.assetDetails && (
                                    <>
                                        <div className="mb-3">
                                            <div className="text-white-50 small mb-1">Format</div>
                                            <div className="text-white">
                                                <span className="badge bg-secondary me-1">{order.version.assetDetails.resolution}</span>
                                                <span className="badge bg-secondary me-1">{order.version.assetDetails.dimensions}</span>
                                                <span className="badge bg-secondary">{order.version.assetDetails.ratio}</span>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <div className="text-white-50 small mb-1">Audio</div>
                                            <div className="text-white">
                                                <span className="badge bg-secondary me-1">{order.version.assetDetails.audioConfig}</span>
                                                {order.version.assetDetails.audioLanguage?.length > 0 && (
                                                    <span className="badge bg-secondary">{order.version.assetDetails.audioLanguage.join(', ').toUpperCase()}</span>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                {order.status === 'request-pending' && isLatestPurchase && (
                                    <div className="d-flex justify-content-center gap-3">
                                        <Button color="success" onClick={() => toggleApprovalModal('approve')} className="px-4">
                                            <span className="fas fa-check-circle me-2" />
                                            Define terms
                                        </Button>
                                        <Button color="danger" outline onClick={() => toggleApprovalModal('decline')} className="px-4">
                                            <span className="fas fa-times-circle me-2" />
                                            Decline
                                        </Button>
                                    </div>
                                )}
                            </Col>
                        </Row>

                        {currentPurchase?.terms && (
                            <div className="mb-0">
                                <div className="text-white-50 small mb-1">Screening Terms</div>
                                <div className="p-3 bg-black bg-opacity-50 rounded text-white" style={{ fontSize: '0.9rem' }}>
                                    <p>
                                        <span className="fas fa-quote-left me-2 text-primary" />
                                        <i>"{currentPurchase.terms}"</i>
                                    </p>
                                    {/* firstName, not the object: an unpopulated
                                        id is truthy and would print "undefined". */}
                                    {currentPurchase.approvedBy?.firstName && (
                                        <p className="text-primary pt-2 mb-0">
                                            Approved by {currentPurchase.approvedBy.firstName} {currentPurchase.approvedBy.lastName}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {isRevertible() && isLatestPurchase && (
                            <div className="mt-3 d-flex justify-content-end gap-2">
                                <Button color="primary" outline onClick={openAmendModal} className="px-4">
                                    <span className="fas fa-calendar me-2" />
                                    Amend screening date
                                </Button>
                                <Button color="warning" outline onClick={() => setRevertModal(true)} className="px-4">
                                    <span className="fas fa-undo me-2" />
                                    Revert & Refund
                                </Button>
                            </div>
                        )}
                    </div>
                </Col>

                {/* Activity Timeline */}
                <Col lg={6}>
                    <div className="bg-broken border-start border-primary border-3 p-3 h-100 shadow-sm">
                        <h5 className="text-primary mb-3 d-flex align-items-center">
                            <span className="fas fa-history me-2" />
                            Activity Timeline
                        </h5>

                        <PurchaseTimeline purchase={currentPurchase} orderType={order.orderType} />
                    </div>
                </Col>
            </Row>

            {/* Transactions Section */}
            {(order.revertTransaction || order.bacCorrectionTransaction || order.downpaymentTransaction) && (
                <div className="mt-3">
                    <h5 className="text-primary mb-3 text-uppercase">
                        <span className="fas fa-list-check me-2" />
                        Transactions
                    </h5>

                    <div className="transactions-list">
                        {order.revertTransaction && (
                            <div className={`transaction-item bg-broken px-3 py-2 border-start border-end mb-2 pointer justify-content-between align-items-center ${order.revertTransaction.transactionType} d-flex ${order.revertTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                <div className="text-uppercase">
                                    <strong>
                                        <span className="fas fa-file-text me-2 text-primary" />
                                        {order.revertTransaction.description}
                                    </strong>
                                    <br />
                                    <small className="text-white-50">{new Date(order.revertTransaction.effectiveDate).toLocaleString()}</small>
                                </div>

                                <div className="ba-transaction small text-white">
                                    {formatCredits(order.revertTransaction.balanceBeforeTransaction)}
                                    <span className="text-primary mx-2"> | </span>
                                    <span className="text-primary fas fa-arrow-right me-2" />
                                    {formatCredits(order.revertTransaction.balanceAfterTransaction)}
                                </div>

                                <span className="amount lead fw-bold">
                                    <span className={`text-uppercase p-1 small ${order.revertTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'} me-2`}>
                                        <span className={`fas me-2 ${order.revertTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`} />
                                        {formatCredits(order.revertTransaction.amount)}
                                    </span>
                                </span>
                            </div>
                        )}

                        {order.bacCorrectionTransaction && (
                            <div className={`transaction-item bg-broken px-3 py-2 border-start border-end mb-2 pointer justify-content-between align-items-center ${order.bacCorrectionTransaction.transactionType} d-flex ${order.bacCorrectionTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                <div className="text-uppercase">
                                    <strong>
                                        <span className="fas fa-file-text me-2 text-primary" />
                                        {order.bacCorrectionTransaction.description}
                                    </strong>
                                    <br />
                                    <small className="text-white-50">{new Date(order.bacCorrectionTransaction.effectiveDate).toLocaleString()}</small>
                                </div>

                                <div className="ba-transaction small text-white">
                                    {formatCredits(order.bacCorrectionTransaction.balanceBeforeTransaction)}
                                    <span className="text-primary mx-2"> | </span>
                                    <span className="text-primary fas fa-arrow-right me-2" />
                                    {formatCredits(order.bacCorrectionTransaction.balanceAfterTransaction)}
                                </div>

                                <span className="amount lead fw-bold">
                                    <span className={`text-uppercase p-1 small ${order.bacCorrectionTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'} me-2`}>
                                        <span className={`fas me-2 ${order.bacCorrectionTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`} />
                                        {formatCredits(order.bacCorrectionTransaction.amount)}
                                    </span>
                                </span>
                            </div>
                        )}

                        {order.downpaymentTransaction && (
                            <div className={`transaction-item bg-broken px-3 py-2 border-start border-end mb-2 pointer justify-content-between align-items-center ${order.downpaymentTransaction.transactionType} d-flex ${order.downpaymentTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                <div className="text-uppercase">
                                    <strong>
                                        <span className="fas fa-file-text me-2 text-primary" />
                                        {order.downpaymentTransaction.description}
                                    </strong>
                                    <br />
                                    <small className="text-white-50">{new Date(order.downpaymentTransaction.effectiveDate).toLocaleString()}</small>
                                </div>

                                <div className="ba-transaction small text-white">
                                    {formatCredits(order.downpaymentTransaction.balanceBeforeTransaction)}
                                    <span className="text-primary mx-2"> | </span>
                                    <span className="text-primary fas fa-arrow-right me-2" />
                                    {formatCredits(order.downpaymentTransaction.balanceAfterTransaction)}
                                </div>

                                <span className="amount lead fw-bold">
                                    <span className={`text-uppercase p-1 small ${order.downpaymentTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'} me-2`}>
                                        <span className={`fas me-2 ${order.downpaymentTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`} />
                                        {formatCredits(order.downpaymentTransaction.amount)}
                                    </span>
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Purchase History — this title has been bought {purchases.length} times by this cinema */}
            {purchases.length > 0 && (
                <div className="mt-3">
                    <h5 className="text-primary mb-3 text-uppercase">
                        <span className="fas fa-history me-2" />
                        Purchase History
                        <span className="text-white-50 ms-3 text-lowercase" style={{ fontSize: '0.85rem' }}>
                            {purchases.length} purchase{purchases.length === 1 ? '' : 's'} of this title by this cinema
                        </span>
                    </h5>
                    <PurchaseHistory
                        purchases={purchases}
                        isBAC={true}
                        selectedIndex={selectedIndex}
                        onSelect={selectPurchase}
                        status={order.statusWorkflow}
                    />
                </div>
            )}

            <div className="pb-5" />

            <Modal isOpen={approvalModal.isOpen} toggle={toggleApprovalModal}>
                <ModalHeader toggle={toggleApprovalModal} className="text-primary">Request approval</ModalHeader>
                <ModalBody>
                    {approvalModal.operation === 'approve' && (
                        <div>
                            <Label>Screening terms</Label>

                            <Input
                                type="textarea"
                                className="py-3"
                                value={screeningTerms}
                                onChange={e => setScreeningTerms(e.target.value)}
                            />

                            <Label className="mt-3">Screening date & time</Label>
                            <DateTimeField
                                value={bacCorrection.screeningDateCorrection || new Date(order.screeningDate)}
                                onChange={(datetime) =>
                                    setBacCorrection((prevState) => ({
                                        ...prevState,
                                        screeningDateCorrection: datetime,
                                    }))
                                }
                                step={15}
                            />

                            <Label className="mt-3">Hire Period</Label>
                            <div className="d-flex">
                                <Input
                                    value={bacCorrection.defaultHirePeriodCorrection}
                                    name="defaultHirePeriodCorrection"
                                    type="number"
                                    onChange={handleChange}
                                />

                                <select className="form-select-small ms-3"
                                    value={bacCorrection.hirePeriodTypeCorrection} name="hirePeriodTypeCorrection"
                                    onChange={handleChange}>
                                    <option value="days">Days</option>
                                    <option value="hours">Hours</option>
                                </select>
                            </div>

                            <Label className="mt-3">Price correction</Label>
                            <Input
                                type="number"
                                className="py-3"
                                name="priceCorrection"
                                value={bacCorrection.priceCorrection}
                                onChange={handleChange}
                            />

                            <div className="mt-3" />

                            {!!Number(bacCorrection.priceCorrection) && (
                                <Fragment>
                                    <div className="d-flex">
                                        <Label className="flex-grow-1">
                                            Initial amount:
                                        </Label>
                                        <span>{order.downpayment} credits</span>
                                    </div>

                                    <div className="d-flex">
                                        <Label className="flex-grow-1">
                                            Correction:
                                        </Label>
                                        <span
                                            className={bacCorrection.priceCorrection > 0 ? 'text-success' : 'text-danger'}>
                                            {`${Number(bacCorrection.priceCorrection)} credits`}
                                        </span>
                                    </div>
                                </Fragment>
                            )}

                            <div className="d-flex">
                                <Label className="flex-grow-1">
                                    Final amount:
                                </Label>
                                {/*<span>{`${computeOrderPrice(order.downpayment, 'number') + Number(bacCorrection.priceCorrection)} credits`}</span>*/}
                                <span>{order.downpayment + Number(bacCorrection.priceCorrection)} credits</span>
                            </div>
                        </div>
                    )}

                    {approvalModal.operation === 'decline' && (
                        <div>
                            <Label>Reason for decline</Label>
                            <Input
                                type="textarea"
                                className="py-3"
                                value={declineReason}
                                onChange={e => setDeclineReason(e.target.value)}
                            />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color={approvalModal.operation === 'approve' ? 'success' : 'danger'}
                        outline
                        onClick={handleRequestApproval}
                    >
                        <span
                            className={`fas ${approvalModal.operation === 'approve' ? 'fa-check-circle' : 'fa-times-circle'} me-2`} />
                        {approvalModal.operation === 'approve' ? 'Approve' : 'Decline'}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={amendModal} toggle={() => setAmendModal(!amendModal)} size="lg">
                <ModalHeader toggle={() => setAmendModal(false)} className="text-primary">
                    Amend screening date
                </ModalHeader>
                <ModalBody>
                    <Label>
                        Screening date & time
                        {order.organization?.timezone && (
                            <span className="text-white-50 ms-2" style={{ fontWeight: 'normal' }}>
                                (Organization timezone: {order.organization.timezone})
                            </span>
                        )}
                    </Label>
                    <DateTimeField
                        value={amendDate}
                        onChange={(datetime) => setAmendDate(datetime)}
                        step={15}
                    />

                    {amendDate && order.organization?.timezone && (
                        <div className="mt-2 small text-white-50">
                            <span className="fas fa-globe me-2" />
                            In {order.organization.timezone}: <strong className="text-white">{formatInTimezone(amendDate, order.organization.timezone)}</strong>
                        </div>
                    )}

                    <div className="mt-4 p-3 bg-black/50 rounded">
                        <div className="text-primary small mb-2">
                            <span className="fas fa-key me-2" />
                            KDM validity
                        </div>
                        {kdmLoading && (
                            <div className="text-white-50 small">Loading KDM…</div>
                        )}
                        {!kdmLoading && !kdm && (
                            <div className="text-white-50 small fst-italic">No KDM found for this order</div>
                        )}
                        {!kdmLoading && kdm && (
                            <Fragment>
                                <div className="text-white small mb-1">
                                    <strong className="text-primary">Valid from: </strong>
                                    {formatInTimezone(kdm.KDM_notValidBefore, order.organization?.timezone)}
                                </div>
                                <div className="text-white small">
                                    <strong className="text-primary">Valid until: </strong>
                                    {formatInTimezone(kdm.KDM_notValidAfter, order.organization?.timezone)}
                                </div>
                                {isOutsideKdmWindow() && (
                                    <div className="mt-2 text-danger small">
                                        <span className="fas fa-triangle-exclamation me-2" />
                                        Selected screening time is outside the KDM validity window.
                                    </div>
                                )}
                            </Fragment>
                        )}
                    </div>

                    <Label className="mt-3">Notes (optional)</Label>
                    <Input
                        type="textarea"
                        className="py-3"
                        value={amendNotes}
                        onChange={e => setAmendNotes(e.target.value)}
                        placeholder="Reason for the amendment"
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setAmendModal(false)}>
                        Cancel
                    </Button>
                    <Button color="primary" outline onClick={handleAmend} disabled={!amendDate}>
                        <span className="fas fa-calendar-check me-2" />
                        Save new screening date
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={revertModal} toggle={() => setRevertModal(!revertModal)}>
                <ModalHeader toggle={() => setRevertModal(false)} className="text-primary">Revert order & refund</ModalHeader>
                <ModalBody>
                    <Label>Reason for revert</Label>
                    <Input
                        type="textarea"
                        className="py-3"
                        value={revertReason}
                        onChange={e => setRevertReason(e.target.value)}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        outline
                        onClick={handleRevert}
                    >
                        <span className="fas fa-undo me-2" />
                        Revert order & refund
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default OrderDetailsBAC;
